import {
  EnrichedCompositionDetailDto,
  EnrichedVideoEditorEditListItemDto,
} from '@app/modules/compose/composition.types';
import { QueriesView } from '@mi-front/ui';

import { SocialExportUploadLayoutSkeleton } from '@app/modules/social-export/publish';
import { ComposeSocialExportUploadPageContent } from './ComposeSocialExportUploadPageContent';
import {
  type UseComposeSocialExportUploadPageResult,
  useComposeSocialExportUploadPage,
} from './useComposeSocialExportUploadPage';

/** Wires async queries to the social export upload UI for a composition and platform. */
export function ComposeSocialExportUploadPage() {
  const uploadPage: UseComposeSocialExportUploadPageResult = useComposeSocialExportUploadPage();

  if (uploadPage.isResolvingPlatform || !uploadPage.platform) {
    return <SocialExportUploadLayoutSkeleton />;
  }

  if (!uploadPage.areConnectionsResolved) {
    return <SocialExportUploadLayoutSkeleton />;
  }

  if (!uploadPage.selectedConnectionId && uploadPage.connections.length > 0) {
    return <SocialExportUploadLayoutSkeleton />;
  }

  // settingsQuery is intentionally omitted: changing the selected connection refetches
  // settings, and gating the whole page on it would remount into the skeleton.
  // Empty connections are handled inside content so the platform switcher stays visible.
  return (
    <QueriesView
      loader={<SocialExportUploadLayoutSkeleton />}
      queries={[uploadPage.compositionQuery, uploadPage.editsListQuery] as never}
    >
      {(raw) => {
        const [composition, edits] = raw as [
          EnrichedCompositionDetailDto,
          EnrichedVideoEditorEditListItemDto[],
        ];
        return (
          <ComposeSocialExportUploadPageContent
            composition={composition}
            settingsQuery={uploadPage.settingsQuery}
            platform={uploadPage.platform!}
            selectedSource={uploadPage.selectedSource}
            onSelectedSourceChange={uploadPage.setSelectedSource}
            edits={edits}
            hasAvailableSources={uploadPage.hasAvailableSources}
            showMediaSelector={uploadPage.showMediaSelector}
            hasSelectedRenderedEdit={uploadPage.hasSelectedRenderedEdit}
            renderedVideoUrl={uploadPage.renderedVideoUrl}
            PublishForm={uploadPage.PublishForm!}
            onUpload={(data) => uploadPage.handleUpload(composition, data)}
            connections={uploadPage.connections}
            selectedConnectionId={uploadPage.selectedConnectionId}
            onSelectedConnectionChange={uploadPage.setSelectedConnectionId}
          />
        );
      }}
    </QueriesView>
  );
}
